//src/pages/shared/SharedTransactionsPage.js
import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import {
  Typography,
  Container,
  CircularProgress,
  Box,
  Tabs,
  Tab,
  TextField,
  Grid,
  Pagination,
  Snackbar,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Collapse,
  Alert,
} from "@mui/material";
import { FormControlLabel } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { useSearchParams, useLocation, useNavigate } from "react-router-dom";
import api from "../../api/axios";
import TransactionTable from "../../components/TransactionTable";
import { useSnackbar } from "notistack";

const columnOptions = [
  { key: "secret_code", label: "Code" },
  { key: "sender_name", label: "Expéditeur" },
  { key: "receiver_name", label: "Destinataire" },
  { key: "amount", label: "Montant" },
  { key: "fee", label: "Commission" },
  { key: "status", label: "Statut" },
  { key: "sender_agency_name", label: "Agence" },
  { key: "created_by_name", label: "Agent" },
  { key: "sender_location_name", label: "Provenance" },
  { key: "receiver_location_name", label: "Destination" },
  { key: "created_at", label: "Date" },
];

const rowsPerPage = 100;
const SEARCH_DEBOUNCE_MS = 500;
const DEFAULT_TIMEZONE = "Africa/Kinshasa";

const TIMEZONE_OPTIONS = [
  { value: "Africa/Kinshasa", label: "Kinshasa" },
  { value: "Asia/Shanghai", label: "Chine" },
  { value: "UTC", label: "UTC" },
];

const SharedTransactionsPage = ({ user }) => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  const [scope, setScope] = useState("daily");
  const [tab, setTab] = useState("all");

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [agentFilter, setAgentFilter] = useState("");
  const [agencyFilter, setAgencyFilter] = useState("");
  const [timezoneFilter, setTimezoneFilter] = useState(DEFAULT_TIMEZONE);

  const [senderLocation, setSenderLocation] = useState("");
  const [receiverLocation, setReceiverLocation] = useState("");

  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [summary, setSummary] = useState({
  count: 0,
  amount: 0,
  fee: 0,
  grandTotal: 0,
});

  const [noDataMessage, setNoDataMessage] = useState("");
  const [notif, setNotif] = useState("");
  const [showColToggle, setShowColToggle] = useState(false);

  const [initialized, setInitialized] = useState(false);

  const [agencies, setAgencies] = useState([]);
  const [locations, setLocations] = useState([]);

  const tableRef = useRef(null);
  const { enqueueSnackbar } = useSnackbar();
  const [searchParams, setSearchParams] = useSearchParams();

  const location = useLocation();
  const navigate = useNavigate();

  const defaultVisible = useMemo(() => {
    const v = {};
    columnOptions.forEach((c) => {
      if (c.key === "created_by_name" || c.key === "sender_location_name") {
        v[c.key] = false;
      } else {
        v[c.key] = true;
      }
    });
    return v;
  }, []);

  const [visibleCols, setVisibleCols] = useState(() => {
    try {
      const fromStorage = localStorage.getItem("tx_visible_cols_v1");
      if (fromStorage) return JSON.parse(fromStorage);
    } catch (e) {}
    return defaultVisible;
  });

  useEffect(() => {
    const loadFilterSources = async () => {
      try {
        const [agencyRes, locRes] = await Promise.all([
          api.get("/agencies").catch(() => null),
          api.get("/settings/locations").catch(() => null),
        ]);

        if (agencyRes && Array.isArray(agencyRes.data)) {
          setAgencies(agencyRes.data);
        }

        if (locRes && Array.isArray(locRes.data)) {
          setLocations(locRes.data);
        }
      } catch (err) {
        console.error("Error loading filters data:", err);
      }
    };

    loadFilterSources();
  }, []);


  useEffect(() => {
  const timer = setTimeout(() => {
    setDebouncedSearch(search.trim());
  }, SEARCH_DEBOUNCE_MS);

  return () => clearTimeout(timer);
}, [search]);

  useEffect(() => {
    const sp = Object.fromEntries([...searchParams.entries()]);

    if (sp.scope) setScope(sp.scope);
    if (sp.tab) setTab(sp.tab);
    if (sp.search) setSearch(sp.search);
    if (sp.page) setPage(Number(sp.page));
    if (sp.timezone) setTimezoneFilter(sp.timezone);
    if (sp.agency) setAgencyFilter(sp.agency);

    if (sp.from) setFromDate(dayjs(sp.from));
    if (sp.to) setToDate(dayjs(sp.to));
    if (sp.agent_id) setAgentFilter(sp.agent_id);

    if (sp.sender_location) setSenderLocation(sp.sender_location);
    if (sp.receiver_location) setReceiverLocation(sp.receiver_location);

    if (sp.cols) {
      const cols = sp.cols.split(",");
      setVisibleCols((prev) => {
        const copy = { ...prev };
        Object.keys(copy).forEach((k) => {
          copy[k] = cols.includes(k);
        });
        return copy;
      });
    }

    setInitialized(true);
  }, [searchParams]);

  useEffect(() => {
    try {
      localStorage.setItem("tx_visible_cols_v1", JSON.stringify(visibleCols));
    } catch (e) {}

    const colsKeys = Object.entries(visibleCols)
      .filter(([, v]) => v)
      .map(([k]) => k);

    const defaultColsKeys = Object.entries(defaultVisible)
      .filter(([, v]) => v)
      .map(([k]) => k);

    const differ =
      JSON.stringify(colsKeys.slice().sort()) !==
      JSON.stringify(defaultColsKeys.slice().sort());

    const params = Object.fromEntries([...searchParams.entries()]);
    if (differ) params.cols = colsKeys.join(",");
    else delete params.cols;

    setSearchParams(params, { replace: true });
  }, [visibleCols, defaultVisible, searchParams, setSearchParams]);

  const buildParams = useCallback(() => {
    const params = {};
    params.scope = scope;
    params.timezone = timezoneFilter || DEFAULT_TIMEZONE;

    // A direct search must be able to find a transaction regardless of
    // the currently selected status tab.
    if (!debouncedSearch && tab && tab !== "all") params.status = tab;
    if (debouncedSearch) {
      params.search = debouncedSearch;
    }
    if (agentFilter) params.agent_id = agentFilter;
    if (agencyFilter) params.agency = agencyFilter;

    if (senderLocation) params.sender_location = senderLocation;
    if (receiverLocation) params.receiver_location = receiverLocation;

    if (fromDate) params.from = dayjs(fromDate).format("YYYY-MM-DD");
    if (toDate) params.to = dayjs(toDate).format("YYYY-MM-DD");

    params.page = page;
    params.limit = rowsPerPage;

    return params;
  }, [
    scope,
    tab,
    debouncedSearch,
    agentFilter,
    agencyFilter,
    senderLocation,
    receiverLocation,
    fromDate,
    toDate,
    page,
    timezoneFilter,
  ]);

  useEffect(() => {
    if (!initialized) return;

    const params = {};
    if (scope && scope !== "daily") params.scope = scope;
    if (tab && tab !== "all") params.tab = tab;
    if (search && search.trim()) params.search = search.trim();
    if (agentFilter) params.agent_id = agentFilter;
    if (agencyFilter) params.agency = agencyFilter;
    if (timezoneFilter && timezoneFilter !== DEFAULT_TIMEZONE) params.timezone = timezoneFilter;

    if (senderLocation) params.sender_location = senderLocation;
    if (receiverLocation) params.receiver_location = receiverLocation;

    if (fromDate) params.from = dayjs(fromDate).format("YYYY-MM-DD");
    if (toDate) params.to = dayjs(toDate).format("YYYY-MM-DD");

    if (page && Number(page) !== 1) params.page = String(page);

    if (Object.keys(params).length === 0) setSearchParams({}, { replace: true });
    else setSearchParams(params, { replace: true });
  }, [
    initialized,
    scope,
    tab,
    search,
    agentFilter,
    agencyFilter,
    timezoneFilter,
    senderLocation,
    receiverLocation,
    fromDate,
    toDate,
    page,
    setSearchParams,
  ]);

  useEffect(() => {
    const fetchTransactions = async () => {
      setLoading(true);
      try {
        const params = buildParams();
        const res = await api.get("/transactions", { params });

     const data =
  res.data?.transactions ||
  res.data?.data ||
  [];

const total = Number(
  res.data?.total ?? data.length
);

const serverSummary = res.data?.summary || {
  count: total,
  amount: 0,
  fee: 0,
  grandTotal: 0,
};

setTransactions(data);

setSummary({
  count: Number(serverSummary.count || 0),
  amount: Number(serverSummary.amount || 0),
  fee: Number(serverSummary.fee || 0),
  grandTotal: Number(serverSummary.grandTotal || 0),
});

setTotalPages(
  Math.max(
    1,
    Math.ceil(total / rowsPerPage)
  )
);

setNoDataMessage(
  res.data?.message || ""
);
      } catch (err) {
        console.error("Error fetching transactions:", err);
        enqueueSnackbar("Échec de la récupération des transactions", { variant: "error" });
        setTransactions([]);

        setSummary({
  count: 0,
  amount: 0,
  fee: 0,
  grandTotal: 0,
});

setTotalPages(1);
      } finally {
        setLoading(false);
      }
    };

    fetchTransactions();
  }, [buildParams, user?.role, user?.id, enqueueSnackbar]);

  const agentOptions = useMemo(() => {
    const map = new Map();
    transactions.forEach((t) => {
      if (t.created_by_user_id || t.created_by_name) {
        const id = t.created_by_user_id ?? t.created_by_name;
        const name = t.created_by_name ?? "Inconnu";
        if (!map.has(String(id))) map.set(String(id), name);
      }
    });
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [transactions]);

  const agencyOptions = useMemo(() => {
    const map = new Map();

    transactions.forEach((t) => {
      const id = t.sender_agency_id;
      const name = t.sender_agency_name;

      if (id != null && name && !map.has(String(id))) {
        map.set(String(id), name);
      }
    });

    let options = Array.from(map.entries()).map(([id, name]) => ({ id, name }));

    if (options.length === 0) {
      options = (agencies || []).map((a) => ({
        id: String(a.id),
        name: a.name,
      }));
    }

    if (
      agencyFilter &&
      !options.some((a) => String(a.id) === String(agencyFilter))
    ) {
      const fallbackAgency = (agencies || []).find(
        (a) => String(a.id) === String(agencyFilter)
      );

      if (fallbackAgency) {
        options = [
          { id: String(fallbackAgency.id), name: fallbackAgency.name },
          ...options,
        ];
      } else {
        options = [
          { id: String(agencyFilter), name: `Agence ${agencyFilter}` },
          ...options,
        ];
      }
    }

    return options;
  }, [transactions, agencies, agencyFilter]);

  const provenanceOptions = useMemo(() => {
    if (locations.length) {
      return locations
        .map((l) => ({
          label: l.city ? `${l.city}` : String(l.location || ""),
        }))
        .filter((x) => x.label);
    }

    const set = new Set();
    transactions.forEach((t) => {
      const label = t.sender_location_name || t.sender_location || "";
      if (label) set.add(label);
    });
    return Array.from(set).map((label) => ({ label }));
  }, [transactions, locations]);

  const destinationOptions = useMemo(() => {
    if (locations.length) {
      return locations
        .map((l) => ({
          label: l.city ? `${l.city}` : String(l.location || ""),
        }))
        .filter((x) => x.label);
    }

    const set = new Set();
    transactions.forEach((t) => {
      const label = t.receiver_location_name || t.receiver_location || "";
      if (label) set.add(label);
    });
    return Array.from(set).map((label) => ({ label }));
  }, [transactions, locations]);

 
  const toggleColumnVisibility = (key) =>
    setVisibleCols((prev) => ({ ...prev, [key]: !prev[key] }));

  const handleViewDetails = (tx) => {
    const returnTo = location.pathname + location.search;
    navigate(`/transactions/${tx.secret_code}`, { state: { returnTo } });
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 3 }}>
      <Typography variant="h5" gutterBottom>
        Transactions
      </Typography>

      <Box sx={{ mb: 2 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => {
            setTab(v);
            setPage(1);
          }}
        >
          <Tab label="Tous" value="all" />
          <Tab label="À retirer" value="outstanding" />
          <Tab label="Partiellement payé" value="partially_paid" />
          <Tab label="Retiré" value="paid" />
          <Tab label="Annulé" value="cancelled" />
        </Tabs>

        <Grid container spacing={2} alignItems="center" sx={{ mt: 1 }}>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Période</InputLabel>
              <Select
                value={scope}
                label="Période"
                onChange={(e) => {
                  const v = e.target.value;
                  setScope(v);
                  setPage(1);
                  if (v === "daily") {
                    setFromDate(null);
                    setToDate(null);
                  }
                }}
              >
                <MenuItem value="daily">Journalier (Aujourd’hui)</MenuItem>
                <MenuItem value="all">Tous</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Recherche (code / expéditeur / bénéficiaire)"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />
          </Grid>

          <Grid item xs={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Agence</InputLabel>
              <Select
                value={agencyFilter}
                label="Agence"
                onChange={(e) => {
                  setAgencyFilter(e.target.value);
                  setPage(1);
                }}
              >
                <MenuItem value="">Toutes</MenuItem>
                {agencyOptions.map((a) => (
                  <MenuItem key={a.id} value={a.id}>
                    {a.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Fuseau</InputLabel>
              <Select
                value={timezoneFilter}
                label="Fuseau"
                onChange={(e) => {
                  setTimezoneFilter(e.target.value);
                  setPage(1);
                }}
              >
                {TIMEZONE_OPTIONS.map((t) => (
                  <MenuItem key={t.value} value={t.value}>
                    {t.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Agent</InputLabel>
              <Select
                value={agentFilter}
                label="Agent"
                onChange={(e) => {
                  setAgentFilter(e.target.value);
                  setPage(1);
                }}
              >
                <MenuItem value="">Tous</MenuItem>
                {agentOptions.map((a) => (
                  <MenuItem key={a.id} value={a.id}>
                    {a.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Provenance</InputLabel>
              <Select
                value={senderLocation}
                label="Provenance"
                onChange={(e) => {
                  setSenderLocation(e.target.value);
                  setPage(1);
                }}
              >
                <MenuItem value="">Toutes</MenuItem>
                {provenanceOptions.map((p) => (
                  <MenuItem key={p.label} value={p.label}>
                    {p.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Destination</InputLabel>
              <Select
                value={receiverLocation}
                label="Destination"
                onChange={(e) => {
                  setReceiverLocation(e.target.value);
                  setPage(1);
                }}
              >
                <MenuItem value="">Toutes</MenuItem>
                {destinationOptions.map((d) => (
                  <MenuItem key={d.label} value={d.label}>
                    {d.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6} md={2}>
            <DatePicker
              label="From"
              value={fromDate}
              onChange={(val) => {
                setFromDate(val);
                setPage(1);
                if (val) setScope("all");
              }}
              format="YYYY-MM-DD"
            />
          </Grid>

          <Grid item xs={6} md={2}>
            <DatePicker
              label="To"
              value={toDate}
              onChange={(val) => {
                setToDate(val);
                setPage(1);
                if (val) setScope("all");
              }}
              format="YYYY-MM-DD"
            />
          </Grid>
        </Grid>

        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Fuseau utilisé pour la liste : <b>{timezoneFilter}</b>
          </Typography>
        </Box>

        <Collapse in={showColToggle} sx={{ mt: 1 }}>
          <Box display="flex" flexWrap="wrap" gap={2}>
            {columnOptions.map((col) => (
              <FormControl key={col.key} sx={{ minWidth: 140 }} size="small">
                <FormControlLabel
                  control={
                    <input
                      type="checkbox"
                      checked={!!visibleCols[col.key]}
                      onChange={() => toggleColumnVisibility(col.key)}
                    />
                  }
                  label={col.label}
                />
              </FormControl>
            ))}
          </Box>
        </Collapse>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress />
        </Box>
      ) : noDataMessage ? (
        <Alert severity="info" sx={{ mt: 3, textAlign: "center" }}>
          {noDataMessage}
        </Alert>
      ) : transactions.length === 0 ? (
        <Box mt={3} textAlign="center">
          <Typography variant="body1" color="text.secondary">
            Aucune transaction trouvée.
          </Typography>
        </Box>
      ) : (
        <>
        <Box
  sx={{
    mb: 2,
    display: "flex",
    gap: 3,
    flexWrap: "wrap",
  }}
>
  <Typography variant="body2">
    Transactions : <b>{summary.count}</b>
  </Typography>

  <Typography variant="body2">
    Montant :{" "}
    <b>{summary.amount.toFixed(2)}</b>
  </Typography>

  <Typography variant="body2">
    Commission :{" "}
    <b>{summary.fee.toFixed(2)}</b>
  </Typography>

  <Typography variant="body2">
    Total :{" "}
    <b>{summary.grandTotal.toFixed(2)}</b>
  </Typography>
</Box>

          <Box ref={tableRef}>
            <TransactionTable
              transactions={transactions}
              onViewDetails={handleViewDetails}
              onStatusUpdate={async (txOrCode, newStatus) => {
                const secretCode =
                  typeof txOrCode === "string" ? txOrCode : txOrCode.secret_code;
                try {
                  await api.patch(`/transactions/${secretCode}/status`, { status: newStatus });
                  setTransactions((prev) =>
                    prev.map((t) =>
                      t.secret_code === secretCode ? { ...t, status: newStatus } : t
                    )
                  );
                  enqueueSnackbar("Statut mis à jour avec succès.", { variant: "success" });
                } catch (err) {
                  const message =
                    err?.response?.data?.error || "Échec de la mise à jour du statut.";
                  enqueueSnackbar(message, { variant: "error" });
                }
              }}
              visibleCols={visibleCols}
              onToggleColumn={toggleColumnVisibility}
              showColumnToggles={showColToggle}
              onToggleShowColumnToggles={() => setShowColToggle((s) => !s)}
              userRole={user?.role}
              reportTimezone={timezoneFilter}
            />
          </Box>

          <Box display="flex" justifyContent="center" mt={3}>
            <Pagination count={totalPages} page={page} onChange={(_, v) => setPage(v)} />
          </Box>
        </>
      )}

      <Snackbar
        open={!!notif}
        autoHideDuration={3000}
        onClose={() => setNotif("")}
        message={notif}
      />
    </Container>
  );
};

export default SharedTransactionsPage;